interface Endpoints {
  destinations: string;
  luxuryDestinations: string;
  timeSlots: string;
  customerAccount: string;
  createAppointment: string;
  modifyAppointment: string;
  getAppointmentInformation: string;
  createEmailLead: string;
  relevanceSearch: string;
  branches: string;
  nearbyAgents: string;
  locations: string;
}

export const endpointPaths: Endpoints = {
  destinations: '/travel-scheduler/scheduler/v1/destinations',
  luxuryDestinations: '/travel-scheduler/scheduler/v1/destinations/luxury',
  timeSlots: '/travel-scheduler/scheduler/v1/agents-and-timeslots/{workTypeId}',
  nearbyAgents: '/travel-scheduler/scheduler/v1/nearby-agents/{workTypeId}',
  customerAccount: '/travel-scheduler/scheduler/v1/customer-account',
  modifyAppointment:
    '/travel-scheduler/scheduler/v1/modify-appointment/{appointmentId}',
  createAppointment: '/travel-scheduler/scheduler/v1/appointment',
  getAppointmentInformation:
    '/travel-scheduler/scheduler/v1/appointment/{appointmentId}',
  createEmailLead: '/travel-scheduler/scheduler/v1/process-email-inquiry',
  relevanceSearch: '/travel-relevance-search/relevance-search/v1/search',
  branches: '/travel-scheduler/scheduler/v1/branches',
  locations: '/travel-advisor/advisor/v1/locations',
};

export enum Environments {
  test = 'mockapi',
  development = 'mockapi',
  dev = 'dev',
  qa = 'qa',
  uat = 'uat',
  prod = 'prod',
}

export const prodEnvironments: Array<Environments> = [Environments.prod];

export const getApiDomain = (env: Environments): string => {
  switch (env) {
    case Environments.development:
      return 'mockapi';
    case Environments.prod:
      return 'common.api.ace.aaa.com';
    default:
      return `common.api.${env}.ace.aaa.com`;
  }
};

export const isEnvironmentProd = (hostname: string): boolean => {
  return prodEnvironments.includes(getEnvironmentFromHost(hostname));
};

export const getEnvironmentFromHost = (hostname: string): Environments => {
  // Check for ACE Identity mode
  if (import.meta.env.VITE_IDENTITY_MODE === 'true') {
    return Environments.development;
  }

  if (
    hostname.search('localhost') > -1 &&
    (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test')
  ) {
    return Environments.development;
  }

  if (hostname.search('localhost') > -1) {
    return Environments.dev;
  }

  if (hostname.startsWith('appdev1') || hostname.startsWith('appdev2')) {
    return Environments.dev;
  }

  if (hostname.startsWith('appqa1') || hostname.startsWith('appqa2')) {
    return Environments.qa;
  }

  if (hostname.startsWith('appuat1') || hostname.startsWith('appuat2')) {
    return Environments.uat;
  }

  if (hostname.startsWith('app.ace')) {
    return Environments.prod;
  }

  return Environments.uat;
};

const generateEndpoints = (hostname: string): Endpoints => {
  const env = getEnvironmentFromHost(hostname);

  const isAceIdentityEnv = import.meta.env.VITE_IDENTITY_MODE === 'true';

  const protocol: string = isAceIdentityEnv
    ? 'https'
    : ['development', 'test'].includes(process.env.NODE_ENV || 'development')
      ? 'http'
      : 'https';

  const _endpoints: Endpoints = {
    destinations: `${protocol}://${getApiDomain(env)}${
      endpointPaths.destinations
    }`,
    luxuryDestinations: `${protocol}://${getApiDomain(env)}${
      endpointPaths.luxuryDestinations
    }`,
    timeSlots: `${protocol}://${getApiDomain(env)}${endpointPaths.timeSlots}`,
    nearbyAgents: `${protocol}://${getApiDomain(env)}${endpointPaths.nearbyAgents}`,
    customerAccount: `${protocol}://${getApiDomain(env)}${
      endpointPaths.customerAccount
    }`,
    createAppointment: `${protocol}://${getApiDomain(env)}${
      endpointPaths.createAppointment
    }`,
    modifyAppointment: `${protocol}://${getApiDomain(env)}${
      endpointPaths.modifyAppointment
    }`,
    getAppointmentInformation: `${protocol}://${getApiDomain(env)}${
      endpointPaths.getAppointmentInformation
    }`,
    createEmailLead: `${protocol}://${getApiDomain(env)}${
      endpointPaths.createEmailLead
    }`,
    relevanceSearch: `${protocol}://${getApiDomain(env)}${
      endpointPaths.relevanceSearch
    }`,
    branches: `${protocol}://${getApiDomain(env)}${endpointPaths.branches}`,
    locations: `${protocol}://${getApiDomain(env)}${endpointPaths.locations}`,
  };
  return _endpoints;
};

export const getEndpoints = () => generateEndpoints(window.location.hostname);
